import React, {Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useSelector} from 'react-redux';

import {CoreObjectId, DomainGraphType} from 'app-core/framework/CoreObjectConstants';
import {getObjectType} from 'app-core/framework/CoreObjectIntrospectionUtils';
import {assert} from 'Asserts';
import {Capability} from 'auth/capabilities/CapabilityFeatureConstants';
import {isCapabilityEnabledForListing} from 'auth/capabilities/isCapabilityEnabledForListing';
import BenchlingConfig from 'BenchlingConfig';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getAccessibleOrInaccessibleId, isInaccessible} from 'graphql-client/InaccessibleInfo';
import navigateToURL from 'lib/navigateToURL';
import {getVersionsWithPublishableVersionInfos} from 'notebook/version-history/VersionHistoryPanelUtil';
import {Version} from 'notebook/version-history/VersionHistoryTypes';
import notifications from 'notifications';
import {HistoryManagerContext} from 'procedures/design/ProcedureHistoryPanel';
import {
  ProcedurePageFlowchartVersionQuery,
  ProcedurePageFlowchartVersionQueryVariables,
} from 'procedures/graphql/__generated/ProcedurePageFlowchartVersionQuery';
import {
  ProcedurePageThinQuery,
  ProcedurePageThinQuery_procedure_ProcedureNode as ThinNetworkProcedure,
  ProcedurePageThinQuery_procedure_ProcedureNode_procedureFlowchart_ProcedureFlowchartNode as ThinNetworkProcedureFlowchart,
  ProcedurePageThinQuery_procedure_ProcedureNode_procedureFlowchart_ProcedureFlowchartNode_latestFlowchartVersion_ProcedureFlowchartVersionNode as AccessibleLatestFlowchartVersion,
  ProcedurePageThinQueryVariables,
} from 'procedures/graphql/__generated/ProcedurePageThinQuery';
import {
  PROCEDURE_PAGE_FLOWCHART_VERSION_QUERY,
  PROCEDURE_PAGE_THIN_QUERY,
} from 'procedures/graphql/ProceduresQueries';
import {getProcedureVersions} from 'procedures/network/ProcedureDAO';
import {GetCachedCidFunction, IdToCidMap} from 'procedures/network/procedureNetworkUtils';
import {procedurePageUrl} from 'procedures/network/procedureUrls';
import {ProcedureFlowchart, ProcedureMetadata} from 'procedures/ProcedureDomainTypes';
import {generateIdToCidMap} from 'procedures/procedurePagePersistenceUtils';
import getProcedureUIString, {ProceduresStringKeys, StringCase} from 'procedures/ProceduresStringKeys';
import {
  procedureAutosaverBlockedChanged,
  procedureAutosaverPausedChanged,
  procedureDirtyStateChanged,
  procedureFlowchartUpdated,
  procedureIsSaveInFlightStateChanged,
  procedureReadonlyStateChanged,
  ProceduresAction,
} from 'procedures/redux/proceduresActions';
import useProceduresAutosaveManager from 'procedures/redux/useProceduresAutosaveManager';
import {BenchlingState} from 'redux-stores/reduceApp';
import {getStore} from 'redux-stores/Store';
import {PublishingRecordForVersionHistoryPanel} from 'template-collectibles/publishing-ui/graphql/types';
import useFunctionIfMounted from 'ui-library/hooks/useFunctionIfMounted';
import useOnMount from 'ui-library/hooks/useOnMount';
import useStateIfMounted from 'ui-library/hooks/useStateIfMounted';
import {CenteredSpinner} from 'ui-library/Spinner';
import * as logging from 'util/Logging';
import memoizeLastCall from 'util/memoizeLastCall';
import pick from 'util/pick';

type RefetchThinProcedure = () => null | Promise<ThinNetworkProcedure | null>;

// This type represents a "thick" procedure: a "thin" procedure augmented with its fully hydrated flowchart data.
export type NetworkProcedure = ThinNetworkProcedure & {
  procedureFlowchart: ThinNetworkProcedureFlowchart & {
    latestFlowchartVersion: ProcedurePageFlowchartVersionQuery['procedureFlowchartVersion'];
  };
};

export const INACCESSIBLE_FLOWCHART_VERSION_ID = 'INACCESSIBLE';

function isProcedureVersionPoW(version: string): boolean {
  return getObjectType({id: version as CoreObjectId}) === DomainGraphType.WORKFLOW_FLOWCHART_CONFIG_VERSION;
}

/**
 * This component fetches and tracks the procedure versions, including which one the user is viewing.
 * Note that the trigger to open the history panel is elsewhere.
 *
 * TODO(BNCH-137457): This component needs to be refactored to support PoW publishing. For now, it won't be updated
 * to support both legacy and PoW publishing. This will be done once we are ready to support PoW publishing.
 */
export function ProcedureVersionHistoryManager(props: {
  procedureId: string;
  flowchartConfigId?: string | null;
  latestVersionId: string | typeof INACCESSIBLE_FLOWCHART_VERSION_ID;
  flowchartVersionId?: string;
  publishedFlowchartVersionId?: string;
  publishingRecords: Array<PublishingRecordForVersionHistoryPanel>;
  children: (versionIdToView: string | undefined, setVersion: (versionId: string) => void) => JSX.Element;
}): JSX.Element {
  // Get the initial version to view
  const anchorVersion: string = (() => {
    if (props.flowchartVersionId != null) {
      return props.flowchartVersionId;
    } else if (props.latestVersionId !== INACCESSIBLE_FLOWCHART_VERSION_ID) {
      return props.latestVersionId;
    } else {
      assert(
        props.publishedFlowchartVersionId != null,
        'Expected published flowchart version to exist or latest version to be accessible'
      );
      // Fall back to the published version if the latest version is inaccessible
      return props.publishedFlowchartVersionId;
    }
  })();

  const [versionIdToView, _setVersionIdToView] = useState<string | undefined>(
    anchorVersion !== props.latestVersionId ? anchorVersion : undefined
  );
  const [fetchedVersions, setFetchedVersions] = useState<null | Array<Version>>(null);

  // For convenience: if the requested version is the latest, use undefined; otherwise, track the version ID explicitly
  const maybeVersionIdToView = useCallback(
    (v: string | undefined): string | undefined => (v === props.latestVersionId ? undefined : v),
    [props.latestVersionId]
  );

  const setVersionIdToView = useCallback(
    (versionId: string): void => {
      const newVersionId = maybeVersionIdToView(versionId);
      // We change the URL and add it to the browser's history, but we don't want it to trigger a new page load
      navigateToURL(procedurePageUrl(props.procedureId, {procedureFlowchartVersionId: newVersionId}), {
        skipTrigger: true,
      });
      _setVersionIdToView(newVersionId);
    },
    [props.procedureId, maybeVersionIdToView, _setVersionIdToView]
  );

  useEffect(() => {
    // If we're falling back to the published version, call setVersionIdToView to update the URL
    if (props.publishedFlowchartVersionId === anchorVersion) {
      setVersionIdToView(props.publishedFlowchartVersionId);
    }
  }, [props.publishedFlowchartVersionId, anchorVersion, setVersionIdToView]);

  const fetchNow = async (): Promise<void> => {
    const versions = await getProcedureVersions(
      isProcedureVersionPoW(anchorVersion) && props.flowchartConfigId != null
        ? {flowchartConfigId: props.flowchartConfigId, openFlowchartVersionId: anchorVersion}
        : {procedureApiId: props.procedureId, openFlowchartVersionId: anchorVersion}
    );
    setFetchedVersions(getVersionsWithPublishableVersionInfos(versions, props.publishingRecords));
  };

  return (
    <HistoryManagerContext.Provider
      // This context value has a custom deep-equality test, so memoization isn't needed
      // eslint-disable-next-line react/jsx-no-constructed-context-values
      value={{
        fetchNow,
        fetchedVersions,
        versionIdToView,
        setVersionIdToView,
      }}
    >
      {props.children(maybeVersionIdToView(versionIdToView), setVersionIdToView)}
    </HistoryManagerContext.Provider>
  );
}

export type GetCachedCidFunctionFactory = (map?: IdToCidMap) => GetCachedCidFunction;
export type SetIdToCidMapFunction = Dispatch<SetStateAction<IdToCidMap>>;

/**
 * This hook is a thin wrapper around a state value that holds an ID-to-CID map.
 * It also provides a tool for retrieving a CID from an ID.
 */
export function useProcedurePageIdToCidMap(procedureGraphQL: NetworkProcedure): {
  getCachedCidFactory: GetCachedCidFunctionFactory;
  idToCidMap: IdToCidMap;
  setIdToCidMap: SetIdToCidMapFunction;
} {
  // We memoize this value to avoid calling the generate() function more than once
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const initialIdToCidMap = useMemo(() => generateIdToCidMap(procedureGraphQL), []);
  const [idToCidMap, setIdToCidMap] = useStateIfMounted<Record<string, string>>(initialIdToCidMap);

  // The caller can provide a specific map to validate against. Otherwise, use our own state value
  const getCachedCidFactory: GetCachedCidFunctionFactory = useCallback(
    (map?: IdToCidMap) =>
      (id, errorContext): string => {
        const cid = (map ?? idToCidMap)[id];
        assert(cid != null, 'Missing cid in procedure', {idWithMissingCid: id, ...errorContext});
        return cid;
      },
    [idToCidMap]
  );

  return {getCachedCidFactory, idToCidMap, setIdToCidMap};
}

/**
 * This component owns the lifecycle for the autosave manager. If the autosave manager hasn't loaded -- which
 * means this component is mounting while a previous save is still in flight -- we wait to render the children.
 */
export function ProcedureAutosaveManager(props: {procedureId: string; children: JSX.Element}): JSX.Element {
  const hasAutosaveManagerLoaded = useProceduresAutosaveManager(props.procedureId);
  return hasAutosaveManagerLoaded ? props.children : <CenteredSpinner />;
}

const memoizedMergeFlowchart = memoizeLastCall(
  (
    procedure: ThinNetworkProcedure,
    flowchartVersion: ProcedurePageFlowchartVersionQuery['procedureFlowchartVersion']
  ) => mergeFlowchartVersionIntoProcedure(procedure, flowchartVersion),
  {useDeepEquality: true}
);

/**
 * While this component doesn't do anything on its own, it provides a way to trigger a full refresh of the procedure.
 * Its internal counter is used as a `key` prop to force a reload of the entire component tree.
 *
 * In practice, it is used in response to a new flowchart version being generated on the server, rather than on the
 * client, such as when the user creates a new draft of a published procedure.
 */
export function ProcedureHardRefreshManager(props: {
  children: (hardRefresh: () => void, refreshKey: string) => JSX.Element;
}): JSX.Element {
  const [counter, setCounter] = useState(Date.now());
  const hardRefresh = useCallback((): void => {
    setCounter((c) => c + 1);
  }, []);
  return props.children(hardRefresh, `hr-${counter}`);
}

/**
 * ProcedureLoader: its only job is to fetch a "thin" procedure (meaning: without the full flowchart structure).
 * It's structured as a HoC to allow for sequential querying.
 */
export function ProcedureLoader(props: {
  procedureId: string;
  children: (procedureData: ThinNetworkProcedure, refetch: RefetchThinProcedure) => JSX.Element;
}): JSX.Element | null {
  const procedureQueryResult = useBenchlingQuery<ProcedurePageThinQuery, ProcedurePageThinQueryVariables>(
    PROCEDURE_PAGE_THIN_QUERY,
    {
      variables: {procedureId: props.procedureId},
      renderCachedDataWhenRefetching: true,
      // Ensure that we /always/ do a network request for this query. This is needed when a new draft is
      // created, and we very much don't want to use the cached procedure. For normal editing operations,
      // this will be transparent to the user.
      fetchPolicy: 'network-only',
    }
  );
  const {type: resultType, refetch: resultRefetch} = procedureQueryResult;

  const refetch = useCallback(async (): Promise<ThinNetworkProcedure | null> => {
    if (resultType !== QueryResultType.SUCCESS) {
      return null;
    }
    const refetchedQuery = await resultRefetch();
    const updatedNetworkProcedure = refetchedQuery.data.procedure;
    assert(!isInaccessible(updatedNetworkProcedure), 'Procedure not viewable after updating');
    return updatedNetworkProcedure;
  }, [resultType, resultRefetch]);

  if (procedureQueryResult.type === QueryResultType.ERROR) {
    notifications.error('Could not load procedure; try reloading the page.');
    logging.error(`Procedure loading error: ${procedureQueryResult.error?.message}`);
    return null;
  } else if (
    // Any other query that intersects with this one will turn this into a pending state for one render cycle,
    // so we need to use result.previousData to avoid unmounting and losing all state in the editor UIs
    procedureQueryResult.type === QueryResultType.PENDING &&
    procedureQueryResult.previousData == null
  ) {
    return <CenteredSpinner />;
  }

  const queryData = procedureQueryResult.data ?? procedureQueryResult.previousData;
  if (queryData == null) {
    return <CenteredSpinner />;
  }
  if (isInaccessible(queryData.procedure)) {
    notifications.error(
      'This procedure does not exist, or you do not have permission to view this procedure.'
    );
    return null;
  } else if (
    // TODO(BNCH-137457): Once we are ready to support PoW publishing, we need to update this validation condition
    // to use conditionally check whether it needs to validate legacy flowchart version or PoW latest flowchart version.
    isInaccessible(queryData.procedure.procedureFlowchart.latestFlowchartVersion) &&
    queryData.procedure.procedureFlowchart.publishedFlowchartVersion == null
  ) {
    assert(
      BenchlingConfig.PROCEDURES_ENABLE_PUBLISHING,
      'Expected latest flowchart version to always be accessible for accessible procedure when publishing is disabled'
    );
    notifications.error(
      `You do not have permission to view unpublished ${getProcedureUIString(
        ProceduresStringKeys.PROCEDURE,
        queryData.procedure.procedureType,
        {stringCase: StringCase.LOWER, plural: false}
      )}.`
    );
    return null;
  }
  return props.children(queryData.procedure, refetch);
}

// Merge the flowchart data into the thin procedure data, to produce a hydrated ("thick") procedure
function mergeFlowchartVersionIntoProcedure(
  procedure: ThinNetworkProcedure,
  flowchartVersion: ProcedurePageFlowchartVersionQuery['procedureFlowchartVersion']
): NetworkProcedure | null {
  assert(!isInaccessible(flowchartVersion), 'Procedure flowchart version is not viewable');

  // Ensure the requested flowchart version is associated with the requested procedure
  if (procedure.procedureFlowchart.id !== flowchartVersion.procedureFlowchart.id) {
    logging.error('Missing parent flowchart ID', {
      extra: {
        currentFC: procedure.procedureFlowchart,
        flowchartVersion,
      },
    });
    notifications.error("The requested flowchart can't be found.");
    return null;
  }

  return {
    ...procedure,
    procedureFlowchart: {
      ...procedure.procedureFlowchart,
      latestFlowchartVersion: flowchartVersion,
    },
  };
}

export interface LoadedFlowchartVersionMetadata {
  id: string;
  createdAt: string;
}

/**
 * ProcedureLegacyFlowchartVersionLoader: its only job is to retrieve the flowchart data for a procedure.
 * If no flowchart version ID is provided, we fall back to the latest version (which is stored in procedureData).
 *
 * Note that in PoW, this component is not needed. Per-version loads are handled by ProcessFlowchartConfigEditor.
 * It exists in the render tree to hydrate the full procedure, which still needs the legacy version data.
 */
export function ProcedureLegacyFlowchartVersionLoader(props: {
  procedureData: ThinNetworkProcedure;
  procedureFlowchartVersionId?: string;
  children: (
    procedureData: NetworkProcedure,
    refetch: (refetchThinProcedure: RefetchThinProcedure) => Promise<NetworkProcedure | null> | null,
    loadedFlowchartVersion: LoadedFlowchartVersionMetadata,
    isViewingOldVersion: boolean
  ) => JSX.Element;
}): JSX.Element | null {
  let procedureFlowchartVersionId: string | null | undefined = props.procedureFlowchartVersionId;

  // This component is only used to load legacy flowchart versions. PoW versions are loaded elsewhere (particularly,
  // in ProcessFlowchartConfigEditor). If we're given a PoW version ID, we ignore it and fall back to load the latest
  // legacy version instead.
  if (procedureFlowchartVersionId != null && isProcedureVersionPoW(procedureFlowchartVersionId)) {
    procedureFlowchartVersionId = null;
  }

  // Keep the latest procedure in a ref, so we can update it inside refetch (see below)
  const currentProcedure = useRef<ThinNetworkProcedure>(props.procedureData);
  if (procedureFlowchartVersionId == null) {
    assert(
      !isInaccessible(currentProcedure.current.procedureFlowchart.latestFlowchartVersion),
      'Expected latest flowchart version to be accessible if no version ID is specified'
    );
  }
  const loadedFlowchartVersion = useRef<LoadedFlowchartVersionMetadata>(
    procedureFlowchartVersionId != null
      ? // The actual "createdAt" will be filled in when the query returns
        {id: procedureFlowchartVersionId, createdAt: 'placeholder'}
      : pick(
          currentProcedure.current.procedureFlowchart
            .latestFlowchartVersion as AccessibleLatestFlowchartVersion,
          'id',
          'createdAt'
        )
  );
  const [isRefetching, setIsRefetching] = useStateIfMounted(false);
  // If no flowchart version ID is provided on props, we fall back to the latest version available
  const getFlowchartVersionIdInPreparationForLoad = (): string => {
    if (procedureFlowchartVersionId != null) {
      return procedureFlowchartVersionId;
    } else {
      // TODO(BNCH-137457): Once we are ready to support PoW publishing, the flowchart version Id we are returning
      // in this function will need to be updated.
      assert(
        !isInaccessible(currentProcedure.current.procedureFlowchart.latestFlowchartVersion),
        'Expected latest flowchart version to be accessible if no version ID is specified'
      );
      return currentProcedure.current.procedureFlowchart.latestFlowchartVersion.id;
    }
  };

  const flowchartVersionQueryResult = useBenchlingQuery<
    ProcedurePageFlowchartVersionQuery,
    ProcedurePageFlowchartVersionQueryVariables
  >(PROCEDURE_PAGE_FLOWCHART_VERSION_QUERY, {
    variables: {procedureFlowchartVersionId: getFlowchartVersionIdInPreparationForLoad()},
    renderCachedDataWhenRefetching: true,
  });

  /**
   * The manual refetch is called whenever the user modifies the flowchart.
   *
   * It does a sequential query to retrieve both the thin procedure and the flowchart data,
   * but since it's a hook, it can do so using a natural syntax: await followed by await.
   */
  const refetch = useFunctionIfMounted(
    async (refetchThinProcedure: RefetchThinProcedure): Promise<NetworkProcedure | null> => {
      if (flowchartVersionQueryResult.type !== QueryResultType.SUCCESS) {
        return null;
      }

      setIsRefetching(true);
      const refetchedThinProcedure = await refetchThinProcedure();
      if (refetchedThinProcedure == null) {
        setIsRefetching(false);
        return null;
      }
      // The new procedure will have a new flowchart version to load
      currentProcedure.current = refetchedThinProcedure;
      const refetchedFlowchartVersion = await flowchartVersionQueryResult.refetch({
        procedureFlowchartVersionId: getFlowchartVersionIdInPreparationForLoad(),
      });
      setIsRefetching(false);

      if (refetchedFlowchartVersion == null) {
        return null;
      }
      assert(
        !isInaccessible(refetchedThinProcedure) &&
          !isInaccessible(refetchedFlowchartVersion.data.procedureFlowchartVersion),
        'Procedure is not viewable after updating'
      );
      return mergeFlowchartVersionIntoProcedure(
        refetchedThinProcedure,
        refetchedFlowchartVersion.data.procedureFlowchartVersion
      );
    }
  );

  if (flowchartVersionQueryResult.type === QueryResultType.ERROR) {
    notifications.error('Could not load flowchart; try reloading the page.');
    logging.error(`Procedure flowchart loading error: ${flowchartVersionQueryResult.error?.message}`);
    return null;
  } else if (
    // Any other query that intersects with this one will turn this into a pending state for one render cycle,
    // so we need to use result.previousData to avoid unmounting and losing all state in the editor UIs
    flowchartVersionQueryResult.type === QueryResultType.PENDING &&
    flowchartVersionQueryResult.previousData == null
  ) {
    return <CenteredSpinner />;
  }

  const queryData = flowchartVersionQueryResult.data ?? flowchartVersionQueryResult.previousData;
  if (queryData == null) {
    return <CenteredSpinner />;
  }

  if (isInaccessible(queryData.procedureFlowchartVersion)) {
    notifications.error(
      'This procedure flowchart does not exist, or you do not have permission to view this procedure.'
    );
    return null;
  }

  loadedFlowchartVersion.current = pick(queryData.procedureFlowchartVersion, 'id', 'createdAt');

  const fullProcedure = memoizedMergeFlowchart(props.procedureData, queryData.procedureFlowchartVersion);

  // When we set this to true, the user will get a banner and will (of course) be locked out of any edits. During
  // a refetch -- which happens during normal operations -- we don't want this! E.g., if the user is editing a step
  // document, the doc will get locked briefly, losing their cursor position, and making editing difficult.
  // TODO(BNCH-137457): Once we are ready to support PoW publishing, the flowchart version Id we are returning
  // in this function will need to be updated.
  const isViewingOldVersion =
    !isRefetching &&
    loadedFlowchartVersion.current.id !==
      getAccessibleOrInaccessibleId(currentProcedure.current.procedureFlowchart.latestFlowchartVersion);

  return fullProcedure
    ? props.children(fullProcedure, refetch, loadedFlowchartVersion.current, isViewingOldVersion)
    : null;
}

/**
 * ProcedureCapabilityChecker: ensures the user has sufficient access
 */
export function ProcedureCapabilityChecker(props: {
  procedureData: ThinNetworkProcedure;
  children: JSX.Element;
}): JSX.Element | null {
  const [shownRegistryAccessError, setShownRegistryAccessError] = useState(false);

  if (!isCapabilityEnabledForListing(Capability.REGISTRY)) {
    if (!shownRegistryAccessError) {
      setShownRegistryAccessError(true);
      notifications.error(
        `You do not have access to the Registry feature, which is required for viewing ${getProcedureUIString(
          ProceduresStringKeys.PROCEDURE,
          props.procedureData.procedureType,
          {
            stringCase: StringCase.LOWER,
            plural: true,
          }
        )}. Please contact your administrator.`
      );
    }
    return null;
  }
  return props.children;
}

interface ProcedureFlowchartStateManagerProps {
  procedureId: string;
  persistedFlowchart: ProcedureFlowchart;
  procedureMetadata: ProcedureMetadata;
  children: (
    persistedFlowchart: ProcedureFlowchart,
    draftFlowchart: ProcedureFlowchart,
    draftFlowchartRef: React.MutableRefObject<ProcedureFlowchart>,
    setDraftFlowchart: (fc: ProcedureFlowchart) => void,
    procedureMetadata: ProcedureMetadata,
    setProcedureMetadata: (procedureMetadata: ProcedureMetadata) => void,
    liveStateChanges: {
      dirtyState: [boolean, (isDirty: boolean) => void];
      readonlyState: [boolean, (isReadonly: boolean) => void];
      isSaveInFlightState: [boolean, (isSaveInFlight: boolean) => void];
      isAutosaverPausedState: [boolean, (isPaused: boolean) => void];
      isAutosaverBlockedState: [boolean, (isBlocked: boolean) => void];
    }
  ) => JSX.Element;
}

function emitAction(action: ProceduresAction): void {
  // Deferring the dispatch ensures we don't trigger a `setState` inside a render cycle
  setTimeout(() => {
    getStore().dispatch(action);
  }, 0);
}

type _FlowchartWithMetadata =
  // Caller can provide one or both of `flowchart` or `procedureMetadata`
  | {
      flowchart: ProcedureFlowchart;
      procedureMetadata?: ProcedureMetadata;
    }
  | {
      flowchart?: ProcedureFlowchart;
      procedureMetadata: ProcedureMetadata;
    };

function sendFlowchartDataToRedux(procedureId: string, flowchartWithMetadata: _FlowchartWithMetadata): void {
  emitAction(procedureFlowchartUpdated({procedureId, ...flowchartWithMetadata}));
}

function setIsProcedureDirty(procedureId: string, isDirty: boolean): void {
  emitAction(procedureDirtyStateChanged(procedureId, isDirty));
}

function setIsProcedureReadonly(procedureId: string, isReadonly: boolean): void {
  emitAction(procedureReadonlyStateChanged(procedureId, isReadonly));
}

function setIsProcedureSaveInFlight(procedureId: string, isSaveInFlight: boolean): void {
  emitAction(procedureIsSaveInFlightStateChanged(procedureId, isSaveInFlight));
}

function setIsAutosaverBlocked(procedureId: string, isBlocked: boolean): void {
  emitAction(procedureAutosaverBlockedChanged(procedureId, isBlocked));
}

function setIsAutosaverPaused(procedureId: string, isPaused: boolean): void {
  emitAction(procedureAutosaverPausedChanged(procedureId, isPaused));
}

/**
 * ProcedureFlowchartStateManager: manages the draft flowchart as the user makes edits. Rather than store
 * draft state directly inside a component like ProcedurePage, we use this component to manage the data in
 * the Redux store. Downstream code doesn't know any better: it can operate as if it were stored in local state.
 */
export function ProcedureFlowchartStateManager(props: ProcedureFlowchartStateManagerProps): JSX.Element {
  // Ensure we push the initial version of the flowchart into Redux. Note that this is async, which is why
  // we have the ?? fallback value immediately below
  useOnMount(() => {
    sendFlowchartDataToRedux(props.procedureId, {
      flowchart: props.persistedFlowchart,
      procedureMetadata: props.procedureMetadata,
    });
  });

  const procedureData = useSelector((state: BenchlingState) => state.models.procedures[props.procedureId]);
  const draftFlowchart = procedureData?.flowchart ?? props.persistedFlowchart;
  const procedureMetadata = procedureData?.procedureMetadata ?? props.procedureMetadata;

  // The setDraftFlowchart callback (below) will update the flowchart kept in redux, where changes will force a
  // re-render. However, we also keep a pointer to the latest flowchart in a ref. The ref is used when sending data
  // to the server. This protects us from stale closures, as our saves happen in async functions, where state is
  // likely to be stale. Unlike state, refs are updated synchronously, so they're never stale.
  const draftFlowchartRef = useRef<ProcedureFlowchart>(props.persistedFlowchart);

  const setDraftFlowchart = useCallback(
    (flowchart: ProcedureFlowchart): void => {
      draftFlowchartRef.current = flowchart;
      sendFlowchartDataToRedux(props.procedureId, {flowchart});
    },
    [props.procedureId]
  );

  const setFlowchartMetadata = useCallback(
    (procedureMetadata: ProcedureMetadata): void => {
      sendFlowchartDataToRedux(props.procedureId, {procedureMetadata});
    },
    [props.procedureId]
  );

  const setIsDirty = useCallback(
    (isDirty: boolean) => {
      setIsProcedureDirty(props.procedureId, isDirty);
    },
    [props.procedureId]
  );

  const setIsReadonly = useCallback(
    (isReadonly: boolean) => {
      setIsProcedureReadonly(props.procedureId, isReadonly);
    },
    [props.procedureId]
  );

  const setIsSaveInFlight = useCallback(
    (isSaveInFlight: boolean) => {
      setIsProcedureSaveInFlight(props.procedureId, isSaveInFlight);
    },
    [props.procedureId]
  );

  const setAutosaverBlocked = useCallback(
    (isBlocked: boolean) => {
      setIsAutosaverBlocked(props.procedureId, isBlocked);
    },
    [props.procedureId]
  );

  const setAutosaverPaused = useCallback(
    (isPaused: boolean) => {
      setIsAutosaverPaused(props.procedureId, isPaused);
    },
    [props.procedureId]
  );

  return props.children(
    props.persistedFlowchart,
    draftFlowchart,
    draftFlowchartRef,
    setDraftFlowchart,
    procedureMetadata,
    setFlowchartMetadata,
    {
      dirtyState: [Boolean(procedureData?.liveProperties.isDirty), setIsDirty],
      readonlyState: [Boolean(procedureData?.liveProperties.isReadOnly), setIsReadonly],
      isSaveInFlightState: [Boolean(procedureData?.liveProperties.isSaveInFlight), setIsSaveInFlight],
      isAutosaverPausedState: [Boolean(procedureData?.liveProperties.isAutosaverPaused), setAutosaverPaused],
      isAutosaverBlockedState: [
        Boolean(procedureData?.liveProperties.isAutosaverBlocked),
        setAutosaverBlocked,
      ],
    }
  );
}
